/**
 * 発注側アプリの枠＝**EC 型シェル**（M1.9・D-98。参照＝Claude Design「建材オーダー_プロダクトUI」）。
 *
 * モバイルファースト（05 §5.3）：タップ領域は最小44px・文字は16px以上・1画面1判断。
 * 現場で手袋のまま片手で押す前提なので、**小さく詰めない**。
 *
 * ★**M1.9（D-98）で「管理画面の枠」から「買い物アプリの枠」へ**：
 *   - 下部タブ 4→**5**＝**注文する／保存済み（A-11）／新規卸追加（A-04）／注文履歴／プロフィール**。
 *     M1.8 までヘッダーに居た「卸一覧・接続」導線はタブへ昇格した（招待コードで卸を増やす操作は
 *     このアプリの営業上いちばん増やしたい操作で、ヘッダーの小さなリンクでは見つからなかった）。
 *   - **カートはタブから外し、ヘッダーのカートボタンへ**（EC の定位置＝右上）。/cart ルート・画面・
 *     バッジ＝全卸合算の品目数（D-83）は不変で、変わるのは入口だけ。D-80「ヘッダーにカートを
 *     置かない」は「タブに常設したので二重になる」が理由＝タブから外す本改修では理由ごと消える。
 *   - **画面タイトルはヘッダーから本文見出しへ**（`PageTitle`）。ヘッダーはロゴ＝「どのアプリか」を
 *     言う場所になる。カタログだけ見出しなし（検索窓とタブのアクティブ表示が代替＝D-98③）。
 * ★**PC（≥1024px）は左サイドバー廃止→EC 型ヘッダー**（FR-123 改・D-98⑤）＝ロゴ・横断検索・
 *   保存済み／注文履歴／新規卸追加／プロフィール／カート。API も状態もモバイルと同じもの。
 * ★**停止中の卸で画面全体を塞がない**（D-83）＝告知は各画面のグループに出す（不変）。
 */

import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useCart } from "./cart";
import { useCartUi } from "./cartUi";
import { useIsDesktop } from "./useIsDesktop";

/**
 * 下部タブ＝**5つ**（D-98①）。デモ（order-app.js）のタブ構成と 1:1。
 *
 * ★ラベルは**リッチメニュー（06 §3.1）と 1:1**＝押した言葉と着いた先が違うと同じ画面だと
 *   気づけない。右下セルは M1.9 で「卸一覧・接続」→**「新規卸追加」**（D-98⑦）。
 */
const TABS = [
  { to: "/catalog", label: "注文する", icon: "catalog" },
  { to: "/sets", label: "保存済み", icon: "sets" },
  { to: "/switch", label: "新規卸追加", icon: "addvendor" },
  { to: "/orders", label: "注文履歴", icon: "orders" },
  { to: "/profile", label: "プロフィール", icon: "profile" },
] as const;

/**
 * PC の EC ヘッダーに並べる項目（M1.9b・Design `.ec-actions`）。
 *
 * ★**中身はタブと同じ5つ**（`tab-*` の testid も 1:1）。違うのは2つだけ：
 *   ①ロゴが「注文する」を兼ねるので `/catalog` は並べない
 *   ②**並びを Design に合わせる**＝注文履歴／新規卸追加／プロフィール／カート。
 *     「保存済み」は Design のヘッダーに無い（カタログ内リンクで代替）が、こちらは
 *     モバイルのタブと 1:1 を崩さないために先頭に残す。
 * ★「新規卸追加」のアイコンだけ PC は店（store）＝Design。下部タブの＋丸は変えない
 *   （リッチメニューの絵柄と揃っているため）。
 */
const DESKTOP_NAV = [
  { to: "/sets", label: "保存済み", icon: "sets" },
  { to: "/orders", label: "注文履歴", icon: "orders" },
  { to: "/switch", label: "新規卸追加", icon: "store" },
  { to: "/profile", label: "プロフィール", icon: "profile" },
] as const;

/** タブ・ヘッダーのアイコン（21px・デモ準拠）。ライブラリは足さない（インライン SVG で足りる）。 */
function TabIcon({ name, size = 21 }: { name: string; size?: number }) {
  const paths: Record<string, JSX.Element> = {
    catalog: (
      <>
        <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
      </>
    ),
    sets: <path d="M18.5 21l-6.5-4.8L5.5 21V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2z" />,
    addvendor: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 8.5v7M8.5 12h7" />
      </>
    ),
    cart: (
      <>
        <path d="M3 4h2.2l2.4 10.5h9.6L19 7H6.2" />
        <circle cx="9.5" cy="19" r="1.4" />
        <circle cx="17" cy="19" r="1.4" />
      </>
    ),
    orders: (
      <>
        <path d="M8.5 5h12M8.5 12h12M8.5 19h12" />
        <circle cx="4" cy="5" r="1.4" />
        <circle cx="4" cy="12" r="1.4" />
        <circle cx="4" cy="19" r="1.4" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8.5" r="3.6" />
        <path d="M4.8 20a7.4 7.4 0 0 1 14.4 0" />
      </>
    ),
    // ★PC の EC ヘッダーだけで使う（Design `I.store`）。**下部タブの `addvendor`（＋丸）は
    //   そのまま**＝スマホの絵を変えると、リッチメニューの絵柄と食い違う（06 §3.1）。
    store: (
      <>
        <path d="M4 4h16l1 5a3 3 0 0 1-3 3 3 3 0 0 1-3-3 3 3 0 0 1-6 0 3 3 0 0 1-3 3 3 3 0 0 1-3-3z" />
        <path d="M5 12v8h14v-8" />
        <path d="M9 20v-5h6v5" />
      </>
    ),
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/** ロゴ（緑の角丸＋3本バー＝デモ order-app.js の logoMark と同じ図形）。 */
function LogoMark({ size = 26 }: { size?: number }) {
  const bar = { display: "block", width: Math.round(size * 0.5), height: 2.5, borderRadius: 2 } as const;
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: "none",
        display: "inline-flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 3,
        background: "var(--kz-color-primary)",
        borderRadius: 7,
      }}
    >
      <span style={{ ...bar, background: "var(--kz-color-ink-inverse)" }} />
      <span style={{ ...bar, background: "var(--kz-color-ink-inverse)", opacity: 0.75 }} />
      <span style={{ ...bar, background: "var(--kz-color-ink-inverse)", opacity: 0.5 }} />
    </span>
  );
}

/**
 * 本文の画面見出し（D-98③）＝ヘッダーからタイトルが消えた分の受け皿。
 * リッチメニューで押した言葉と、着いた画面のこの見出しが同じ語になる（06 §3.1）。
 */
export function PageTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1
      style={{
        margin: "0 0 var(--kz-space-3)",
        fontSize: "var(--kz-font-size-lg)",
        fontWeight: 700,
        lineHeight: 1.4,
      }}
    >
      {children}
    </h1>
  );
}

/**
 * ヘッダーのカートボタン（D-98②）＝バッジは**全卸合算の品目数**（D-83・数量合計ではない）。
 * ★M1.9b（D-101）＝押すと**その場でカートシートが下から出る**（Design と同じ）。/cart への
 *   画面遷移はもう無い＝NavLink ではなく button。
 */
function CartButton({ count, size = 44, desktop = false }: { count: number; size?: number; desktop?: boolean }) {
  const ui = useCartUi();
  return (
    <button
      type="button"
      onClick={ui.openCart}
      data-testid="cart-button"
      aria-label={count > 0 ? `カート（${count}品目）` : "カート"}
      className="kz-pressable"
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "grid",
        placeItems: "center",
        color: "var(--kz-color-ink)",
        borderRadius: "var(--kz-radius-sm)",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        padding: 0,
      }}
    >
      <TabIcon name="cart" />
      {count > 0 && (
        <span
          data-testid="cart-count"
          className="kz-num"
          // ★PC（EC ヘッダー）だけ Design の**赤バッジ**にする（`--danger`・top 0 / right 13）。
          //   緑だと「順調」に読めて「まだ送っていない」の督促にならない、というのが Design の
          //   意図。**スマホ側は M1.9 の緑のまま**＝このレーンは PC 専任で、スマホの見た目は
          //   1px も動かさない（変えるなら SP の版で1回にまとめる）。
          style={{
            position: "absolute",
            top: desktop ? 0 : 1,
            right: desktop ? 13 : 0,
            minWidth: desktop ? 17 : 18,
            padding: "0 5px",
            borderRadius: 999,
            background: desktop ? "var(--kz-status-danger)" : "var(--kz-color-primary)",
            color: "var(--kz-color-ink-inverse)",
            fontSize: desktop ? 10.5 : 11,
            fontWeight: 700,
            lineHeight: desktop ? "17px" : "18px",
            textAlign: "center",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * PC ヘッダーの横断検索（D-98⑤）＝入力すると「注文する」へ移動して絞り込む。
 *
 * ★M1.9b（PC レーン）＝Design `.ec-search`＝**虫めがねを箱の中に入れ、focus で枠が緑・地が白**に
 *   変わる（押す前は沈んだ地＝「ここに打てる」と分かる）。枠の色替えは `:focus-within` が要る＝
 *   inline style では書けないので liff.css の PC ブロックが持つ。
 * ★`data-testid="header-search"` は**input に据え置き**（E2E の指し先は不変）。
 */
function HeaderSearch() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [value, setValue] = useState("");
  return (
    <span className="ec-search">
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        aria-hidden="true"
        style={{ flex: "none" }}
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.5" y2="16.5" />
      </svg>
      <input
        type="search"
        value={value}
        data-testid="header-search"
        // 品番でも引けるようにした（Catalog の `matches()` が `display_no` を見る）＝
        // 案内した探し方が実際に効く。
        placeholder="商品名・品番で検索"
        aria-label="商品を検索（すべての取引先を横断）"
        onChange={(e) => {
          const v = e.target.value;
          setValue(v);
          // 入力のたびにカタログへ＝どの画面からでも検索できる。カタログに居る間は履歴を汚さない
          nav(`/catalog?q=${encodeURIComponent(v)}`, { replace: pathname === "/catalog" });
        }}
      />
    </span>
  );
}

export function Shell() {
  const cart = useCart();
  // ★バッジは**全卸合算の品目数**（D-83）＝カートは1つで、そこに入っている品数が知りたい情報。
  const count = cart.countAll();
  const isDesktop = useIsDesktop();

  // ★PC（≥1024px）は**縮小版でなく専用レイアウト**（FR-123・D-98⑤）＝EC 型ヘッダー。
  //   工務店は事務所の PC からも発注する。**API も状態も同じもの**を使う。
  if (isDesktop) {
    return (
      <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
        <header
          data-testid="desktop-header"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "var(--kz-color-surface)",
            borderBottom: "1px solid var(--kz-color-border)",
            // Design `.ec-head`＝下線に加えてごく薄い影（本文が下を通るときに境目が消えない）
            boxShadow: "0 1px 2px rgba(23, 51, 43, 0.04)",
          }}
        >
          <div
            style={{
              maxWidth: 1320,
              margin: "0 auto",
              padding: "12px var(--kz-space-5) 8px",
              display: "flex",
              alignItems: "center",
              gap: 18,
            }}
          >
            {/* ロゴ＝「注文する」へ（EC の慣習。testid はモバイルのタブと同名＝排他レンダリング） */}
            <NavLink
              to="/catalog"
              data-testid="tab-catalog"
              className="kz-pressable"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--kz-space-2)",
                textDecoration: "none",
                color: "var(--kz-color-ink)",
                flex: "none",
              }}
            >
              <LogoMark size={32} />
              <span style={{ lineHeight: 1.15 }}>
                <span style={{ display: "block", fontWeight: 700, fontSize: "var(--kz-font-size-lg)" }}>
                  建材オーダー
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 10,
                    fontWeight: 600,
                    color: "var(--kz-color-ink-muted)",
                    letterSpacing: "0.05em",
                  }}
                >
                  {/* ★Design の副題＝この画面が「発注」だけでなく「原価管理」まで持つことを
                      ロゴの下で言い切る（PC の主目的が現場ごとの材料費なので） */}
                  工務店向け 発注・原価管理
                </span>
              </span>
            </NavLink>

            <HeaderSearch />

            <nav
              aria-label="メニュー"
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 2, flex: "none" }}
            >
              {DESKTOP_NAV.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  data-testid={`tab-${t.to.slice(1)}`}
                  className="kz-pressable ec-act"
                  style={({ isActive }) => ({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    minWidth: 66,
                    padding: "5px 10px 6px",
                    borderRadius: "var(--kz-radius-sm)",
                    textDecoration: "none",
                    fontSize: 11,
                    // ★太さは**常に 700**（Design）＝選択中は**色だけ**が変わる。太さも変えると
                    //   押すたびにラベルの幅が動いて、隣の項目が left/right にずれる。
                    fontWeight: 700,
                    color: isActive ? "var(--kz-color-primary-hover)" : "var(--kz-color-ink-muted)",
                  })}
                >
                  <TabIcon name={t.icon} size={20} />
                  {t.label}
                </NavLink>
              ))}
              <CartButton count={count} size={52} desktop />
            </nav>
          </div>
        </header>

        <main style={{ flex: 1, width: "100%", maxWidth: 1320, margin: "0 auto", padding: "var(--kz-space-5)" }}>
          <Outlet />
          <p style={{ marginTop: "var(--kz-space-6)" }}>
            <NavLink
              to="/privacy"
              data-testid="privacy-link"
              style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}
            >
              プライバシーポリシー
            </NavLink>
          </p>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
      <header
        // ★すりガラス（M1.7）＝**inline の background は置かない**（クラスに必ず勝ってしまう）。
        className="kz-glassbar"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid var(--kz-color-border)",
          padding: "var(--kz-space-2) var(--kz-space-4)",
          display: "flex",
          alignItems: "center",
          gap: "var(--kz-space-3)",
        }}
      >
        {/* ★ヘッダーは**どのアプリか**を言う（D-98②）。画面の名前は本文見出し（PageTitle）へ。 */}
        <span style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)", minWidth: 0 }}>
          <LogoMark />
          <span style={{ fontWeight: 700, fontSize: "var(--kz-font-size-lg)", whiteSpace: "nowrap" }}>
            建材オーダー
          </span>
        </span>
        <span style={{ marginLeft: "auto", flex: "none" }}>
          {/* カートの入口＝EC の定位置（右上）。バッジ＝品目数（D-83）。 */}
          <CartButton count={count} />
        </span>
      </header>

      <main style={{ flex: 1, padding: "var(--kz-space-4)", paddingBottom: 88 }}>
        <Outlet />
        {/* ★プライバシーポリシーは**常設**（T-D29・09 §K.3）＝どの画面からも1タップで開ける。 */}
        <p style={{ marginTop: "var(--kz-space-6)", textAlign: "center" }}>
          <NavLink
            to="/privacy"
            data-testid="privacy-link"
            style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}
          >
            プライバシーポリシー
          </NavLink>
        </p>
      </main>

      <nav
        aria-label="メニュー"
        // ★すりガラス＋上向きの影（M1.7）＝inline の background は置かない（クラスが効かなくなる）。
        className="kz-glassbar kz-bottombar"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${TABS.length}, 1fr)`,
          borderTop: "1px solid var(--kz-color-border)",
          // iPhone のホームバー分（LIFF は全画面表示）
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            data-testid={`tab-${t.to.slice(1)}`}
            // 押した瞬間に少し縮む（M1.7）＝指に返る。
            className="kz-pressable"
            style={({ isActive }) => ({
              minHeight: 56, // 05 §5.3：タップ領域は最小44px＝下部タブはもっと大きく
              display: "grid",
              placeItems: "center",
              gap: 2,
              textDecoration: "none",
              // 5タブでもラベルが折り返さない大きさ（デモ order-app.css の 9.5px 帯と同じ考え。
              // 「新規卸追加」5文字と「プロフィール」6文字が 390px ÷ 5 枠に収まる実測値）
              fontSize: 10,
              fontWeight: isActive ? 700 : 400,
              whiteSpace: "nowrap",
              color: isActive ? "var(--kz-color-primary)" : "var(--kz-color-ink-muted)",
              borderTop: `3px solid ${isActive ? "var(--kz-color-primary)" : "transparent"}`,
            })}
          >
            <TabIcon name={t.icon} />
            {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
